import classNames from "classnames";
import { SvgSize } from "../../Assets/Svg/React";
import { ButtonColor } from "./buttonColor";
import { ButtonSize } from "./buttonSize";
import { ButtonVariant } from "./buttonVariant";

const getResponsiveSizeClass = (
  breakpoint: string,
  size: ButtonSize,
  baseClass: string
) => (size ? `${breakpoint}:${baseClass}-${size}` : "");

const getColorClasses = (color: ButtonColor, isLoading: boolean) => {
  switch (color ?? ButtonColor.DarkBlue) {
    case ButtonColor.LightBlue:
      return classNames(
        "bg-light-blue-900 text-dark-blue border-light-blue-900",
        "hover:bg-light-blue-600 hover:border-light-blue-600",
        "group-hover:bg-light-blue-600 group-hover:border-light-blue-600",
        {
          "disabled:bg-black/5 disabled:text-black/20 disabled:border-black/5": !isLoading
        }
      );
    case ButtonColor.Turquoise:
      return classNames(
        "bg-turquoise-600 text-dark-blue border-turquoise-600",
        "hover:bg-turquoise-800 hover:border-turquoise-800",
        "group-hover:bg-turquoise-800 group-hover:border-turquoise-800",
        {
          "disabled:bg-black/5 disabled:text-black/20 disabled:border-black/5": !isLoading
        }
      );
    case ButtonColor.Green:
      return classNames(
        "bg-green-600 text-dark-blue border-green-600",
        "hover:bg-green-800 hover:border-green-800",
        "group-hover:bg-green-800 group-hover:border-green-800",
        {
          "disabled:bg-black/5 disabled:text-black/20 disabled:border-black/5": !isLoading
        }
      );
    case ButtonColor.Pink:
      return classNames(
        "bg-pink-200 text-pink-1000 border-pink-200",
        "hover:bg-pink-400 hover:border-pink-400",
        "group-hover:bg-pink-400 group-hover:border-pink-400",
        {
          "disabled:bg-black/5 disabled:text-black/20 disabled:border-black/5": !isLoading
        }
      );
    case ButtonColor.Yellow:
      return classNames(
        "bg-yellow-600 text-dark-blue border-yellow-600",
        "hover:bg-yellow-800 hover:border-yellow-800",
        "group-hover:bg-yellow-800 group-hover:border-yellow-800",
        {
          "disabled:bg-black/5 disabled:text-black/20 disabled:border-black/5": !isLoading
        }
      );
    case ButtonColor.PetrolLight:
      return classNames(
        "bg-petrol-400 text-t-petrol-200 border-petrol-400",
        "hover:bg-petrol-600 hover:border-petrol-600",
        "group-hover:bg-petrol-600 group-hover:border-petrol-600",
        {
          "disabled:bg-black/5 disabled:text-black/20 disabled:border-black/5": !isLoading
        }
      );
    case ButtonColor.PetrolDark:
      return classNames(
        "bg-petrol-900 text-t-petrol-800 border-petrol-900",
        "hover:bg-petrol-600 hover:border-petrol-600",
        "group-hover:bg-petrol-600 group-hover:border-petrol-600",
        {
          "disabled:bg-white/5 disabled:text-white/20 disabled:border-white/5": !isLoading
        }
      );
    case ButtonColor.Neutral:
      return classNames(
        "bg-neutral-200 text-dark-blue border-neutral-200",
        "hover:bg-neutral-300 hover:border-neutral-300",
        "group-hover:bg-neutral-300 group-hover:border-neutral-300",
        {
          "disabled:bg-black/5 disabled:text-black/20 disabled:border-black/5": !isLoading
        }
      );
    case ButtonColor.White:
      return classNames(
        "bg-neutral-0 text-dark-blue border-neutral-0",
        "hover:bg-neutral-200 hover:border-neutral-200",
        "group-hover:bg-neutral-200 group-hover:border-neutral-200",
        {
          "disabled:bg-black/5 disabled:text-black/20 disabled:border-black/5": !isLoading
        }
      );
    case ButtonColor.WhiteOutlineDarkBlue:
      return classNames(
        "bg-neutral-0 text-dark-blue border-dark-blue",
        "hover:bg-light-blue-400 hover:border-dark-blue",
        "group-hover:bg-light-blue-400 group-hover:border-dark-blue",
        "focus-visible:outline-turquoise-800/75",
        {
          "disabled:bg-black/5 disabled:text-black/20 disabled:border-black/5": !isLoading
        }
      );
    case ButtonColor.OutlineDarkBlue:
      return classNames(
        "bg-transparent text-dark-blue border-dark-blue",
        "hover:bg-dark-blue hover:text-neutral-0",
        "group-hover:bg-dark-blue group-hover:text-neutral-0",
        "focus-visible:outline-turquoise-800/75",
        {
          "disabled:text-black/20 disabled:border-black/5": !isLoading
        }
      );
    case ButtonColor.OutlineNeutral:
      return classNames(
        "bg-neutral-0 text-dark-blue border-neutral-200",
        "hover:bg-neutral-200 hover:text-dark-blue",
        "group-hover:bg-neutral-200 group-hover:text-dark-blue",
        {
          "disabled:bg-black/10 disabled:text-black/30 disabled:border-black/10":
            !isLoading
        }
      );
    case ButtonColor.OutlineWhite:
      return classNames(
        "bg-transparent text-neutral-0 border-neutral-0",
        "hover:bg-neutral-0 hover:text-dark-blue",
        "group-hover:bg-neutral-0 group-hover:text-dark-blue",
        {
          "disabled:text-white/50 disabled:border-white/30": !isLoading
        }
      );
    default:
      return classNames(
        "bg-dark-blue text-neutral-0 border-dark-blue",
        "hover:text-turquoise-800",
        "group-hover:text-turquoise-800",
        "focus-visible:outline-turquoise-900",
        {
          "disabled:bg-black/5 disabled:hover:bg-black/5": !isLoading,
          "disabled:text-black/20 disabled:hover:text-black/20": !isLoading,
          "disabled:border-black/5 disabled:hover:border-black/5": !isLoading
        }
      );
  }
};

const getGetSizeClasses = (
  size?: ButtonSize,
  variant?: ButtonVariant,
  sizeSM?: ButtonSize,
  sizeMD?: ButtonSize,
  sizeLG?: ButtonSize,
  sizeXL?: ButtonSize
) =>
  classNames(
    `text-button-${size}`,
    getResponsiveSizeClass("sm", sizeSM ?? ButtonSize.SM, "text-button"),
    getResponsiveSizeClass("md", sizeMD ?? ButtonSize.MD, "text-button"),
    getResponsiveSizeClass("lg", sizeLG ?? ButtonSize.LG, "text-button"),
    getResponsiveSizeClass("xl", sizeXL ?? ButtonSize.XL, "text-button"),
    variant == ButtonVariant.Normal
      ? classNames({
          "py-xxs px-sm": size === ButtonSize.XS,
          "py-xs px-md": size === ButtonSize.SM,
          "py-sm px-lg": size === ButtonSize.MD,
          "py-md px-xl": size === ButtonSize.LG,
          "py-lg px-2xl": size === ButtonSize.XL,

          "sm:py-xxs sm:px-sm": sizeSM === ButtonSize.XS,
          "sm:py-xs sm:px-md": sizeSM === ButtonSize.SM,
          "sm:py-sm sm:px-lg": sizeSM === ButtonSize.MD,
          "sm:py-md sm:px-xl": sizeSM === ButtonSize.LG,
          "sm:py-lg sm:px-2xl": sizeSM === ButtonSize.XL,

          "md:py-xxs md:px-sm": sizeMD === ButtonSize.XS,
          "md:py-xs md:px-md": sizeMD === ButtonSize.SM,
          "md:py-sm md:px-lg": sizeMD === ButtonSize.MD,
          "md:py-md md:px-xl": sizeMD === ButtonSize.LG,
          "md:py-lg md:px-2xl": sizeMD === ButtonSize.XL,

          "lg:py-xxs lg:px-sm": sizeLG === ButtonSize.XS,
          "lg:py-xs lg:px-md": sizeLG === ButtonSize.SM,
          "lg:py-sm lg:px-lg": sizeLG === ButtonSize.MD,
          "lg:py-md lg:px-xl": sizeLG === ButtonSize.LG,
          "lg:py-lg lg:px-2xl": sizeLG === ButtonSize.XL,

          "xl:py-xxs xl:px-sm": sizeXL === ButtonSize.XS,
          "xl:py-xs xl:px-md": sizeXL === ButtonSize.SM,
          "xl:py-sm xl:px-lg": sizeXL === ButtonSize.MD,
          "xl:py-md xl:px-xl": sizeXL === ButtonSize.LG,
          "xl:py-lg xl:px-2xl": sizeXL === ButtonSize.XL
        })
      : classNames({
          "p-xxs": size === ButtonSize.XS,
          "p-xs": size === ButtonSize.SM,
          "p-sm": size === ButtonSize.MD,
          "p-md": size === ButtonSize.LG,
          "p-lg": size === ButtonSize.XL,

          "sm:p-xxs": sizeSM === ButtonSize.XS,
          "sm:p-xs": sizeSM === ButtonSize.SM,
          "sm:p-sm": sizeSM === ButtonSize.MD,
          "sm:p-md": sizeSM === ButtonSize.LG,
          "sm:p-lg": sizeSM === ButtonSize.XL,

          "md:p-xxs": sizeMD === ButtonSize.XS,
          "md:p-xs": sizeMD === ButtonSize.SM,
          "md:p-sm": sizeMD === ButtonSize.MD,
          "md:p-md": sizeMD === ButtonSize.LG,
          "md:p-lg": sizeMD === ButtonSize.XL,

          "lg:p-xxs": sizeLG === ButtonSize.XS,
          "lg:p-xs": sizeLG === ButtonSize.SM,
          "lg:p-sm": sizeLG === ButtonSize.MD,
          "lg:p-md": sizeLG === ButtonSize.LG,
          "lg:p-lg": sizeLG === ButtonSize.XL,

          "xl:p-xxs": sizeXL === ButtonSize.XS,
          "xl:p-xs": sizeXL === ButtonSize.SM,
          "xl:p-sm": sizeXL === ButtonSize.MD,
          "xl:p-md": sizeXL === ButtonSize.LG,
          "xl:p-lg": sizeXL === ButtonSize.XL
        })
  );

export const getCssClass = (
  baseClass: string,
  color: ButtonColor,
  additionalClass?: string,
  size?: ButtonSize,
  variant?: ButtonVariant,
  sizeSM?: ButtonSize,
  sizeMD?: ButtonSize,
  sizeLG?: ButtonSize,
  sizeXL?: ButtonSize,
  isLoading: boolean = false
) =>
  classNames(
    baseClass,
    "flex justify-center items-center border rounded-tl-br-sm",
    "focus-outline",
    // Add programmatic focus outline using data attribute for keyboard accessibility
    "data-[programmatic-focus=true]:outline-4 data-[programmatic-focus=true]:outline-dark-blue/75",
    "transition-short-in hover:transition-short-out",
    "cursor-pointer disabled:cursor-not-allowed",
    getColorClasses(color, isLoading),
    getGetSizeClasses(size, variant, sizeSM, sizeMD, sizeLG, sizeXL),
    {
      "aspect-square": variant == ButtonVariant.Square
    },
    additionalClass
  );

export const getInnerContainerClass = (
  size?: ButtonSize,
  sizeSM?: ButtonSize,
  sizeMD?: ButtonSize,
  sizeLG?: ButtonSize,
  sizeXL?: ButtonSize
) =>
  classNames("relative flex justify-center items-center", "min-h-lg min-w-lg", {
    "gap-xs": size === ButtonSize.SM || size === ButtonSize.XS || size === ButtonSize.MD,
    "gap-md": size === ButtonSize.LG || size === ButtonSize.XL,

    "sm:gap-xs":
      sizeSM === ButtonSize.SM || sizeSM === ButtonSize.XS || sizeSM === ButtonSize.MD,
    "sm:gap-md": sizeSM === ButtonSize.LG || sizeSM === ButtonSize.XL,

    "md:gap-xs":
      sizeMD === ButtonSize.SM || sizeMD === ButtonSize.XS || sizeMD === ButtonSize.MD,
    "md:gap-md": sizeMD === ButtonSize.LG || sizeMD === ButtonSize.XL,

    "lg:gap-xs":
      sizeLG === ButtonSize.SM || sizeLG === ButtonSize.XS || sizeLG === ButtonSize.MD,
    "lg:gap-md": sizeLG === ButtonSize.LG || sizeLG === ButtonSize.XL,

    "xl:gap-xs":
      sizeXL === ButtonSize.SM || sizeXL === ButtonSize.XS || sizeXL === ButtonSize.MD,
    "xl:gap-md": sizeXL === ButtonSize.LG || sizeXL === ButtonSize.XL
  });

export const getIconClass = (
  size?: ButtonSize,
  sizeSM?: ButtonSize,
  sizeMD?: ButtonSize,
  sizeLG?: ButtonSize,
  sizeXL?: ButtonSize
) =>
  classNames({
    "w-md h-md": size === ButtonSize.SM || size === ButtonSize.XS,
    "w-lg h-lg": size === ButtonSize.MD || size === ButtonSize.LG,
    "w-xl h-xl": size === ButtonSize.XL,

    "sm:w-md sm:h-md": sizeSM === ButtonSize.SM || sizeSM === ButtonSize.XS,
    "sm:w-lg sm:h-lg": sizeSM === ButtonSize.MD || sizeSM === ButtonSize.LG,
    "sm:w-xl sm:h-xl": sizeSM === ButtonSize.XL,

    "md:w-md md:h-md": sizeMD === ButtonSize.SM || sizeMD === ButtonSize.XS,
    "md:w-lg md:h-lg": sizeMD === ButtonSize.MD || sizeMD === ButtonSize.LG,
    "md:w-xl md:h-xl": sizeMD === ButtonSize.XL,

    "lg:w-md lg:h-md": sizeLG === ButtonSize.SM || sizeLG === ButtonSize.XS,
    "lg:w-lg lg:h-lg": sizeLG === ButtonSize.MD || sizeLG === ButtonSize.LG,
    "lg:w-xl lg:h-xl": sizeLG === ButtonSize.XL,

    "xl:w-md xl:h-md": sizeXL === ButtonSize.SM || sizeXL === ButtonSize.XS,
    "xl:w-lg xl:h-lg": sizeXL === ButtonSize.MD || sizeXL === ButtonSize.LG,
    "xl:w-xl xl:h-xl": sizeXL === ButtonSize.XL
  });

export const getSpinnerClass = (isLoading: boolean, hasIcon: boolean) =>
  classNames(
    isLoading ? "block" : "hidden",
    "rounded-full animate-spin",
    "[background-image:conic-gradient(transparent_72deg,currentColor_360deg)]",
    "[mask:radial-gradient(farthest-side,transparent_calc(100%-2px),currentColor_0)]",
    {
      "absolute inset-0": hasIcon
    }
  );

/**
 * Maps the button size property to the corresponding SVG size property.
 * @param size The button size (e.g., ButtonSize.XS, ButtonSize.SM, etc.).
 * @returns The SVG size string (e.g., "xs", "sm", "md", "lg", "xl").
 */
export function mapButtonSizeToSvgSize(size?: ButtonSize): SvgSize {
  switch (size) {
    case ButtonSize.XS:
    case ButtonSize.SM:
    case ButtonSize.MD:
      return SvgSize.MD;
    case ButtonSize.LG:
      return SvgSize.LG;
    case ButtonSize.XL:
      return SvgSize.XL;
    default:
      return SvgSize.MD;
  }
}
